import { View } from 'foundations-components/components/View';
import Products from 'merchandise-lib/constants/Product';
import { createToObject } from 'pricing-pages-lib/utils/queryParamUtils';
import { SmartCrmFaqs } from 'pricing-pages-ui-components/frequentlyAskedQuestions/SmartCrmFaqs';
import { PPUIPageHeader } from 'pricing-pages-ui-components/semantic/PageHeader';
import { PageSection } from 'pricing-pages-ui-components/semantic/PageSection';
import CodeSplitEnvWrapper from 'pricing-pages-ui/components/common/env/CodeSplitEnvWrapper';
import { ENTERPRISE as ENTERPRISE_TAB } from 'pricing-pages-ui/constants/routing/Tabs';
import { useCallback, useEffect } from 'react';
import { NavMarker } from 'react-rhumb';
import { Redirect } from 'react-router-dom';
import { ENTERPRISE, PROFESSIONAL } from 'self-service-api/constants/ProductLevels';
import LoadingScreen from '../components/LoadingScreen';
import ProductInfoContainer from '../components/ProductInfoContainer';
import BannerSection from '../components/banners/BannerSection';
import ProductPageSubtitle from '../components/common/ProductPageSubtitle';
import ProductPageTitle from '../components/common/ProductPageTitle';
import FallbackErrorBanner from '../components/common/loading/FallbackErrorBanner';
import SmartCrmFeatureTable from '../components/feature-table/smart-crm-feature-table/SmartCrmFeatureTable';
import HeaderLayout from '../components/layout/HeaderLayout';
import { ProductCardContainer } from '../components/merch-card/card-containers/ProductCardContainer';
import { SMART_CRM_PAGE, SMART_CRM_PAGE_CONTENT } from '../constants/ErrorBoundaryNames';
import { STANDALONE_CORE_SEATS } from '../constants/ReleaseInsights';
import { NavItemEnum } from '../constants/routing/NavItems';
import { useAnchorLinkScroll } from '../hooks/htmlElementHooks';
import { useGlobalLoading } from '../hooks/loadingHooks';
import { useHasReleaseInsight } from '../hooks/useBulkPurchaseOptions';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useRedirectIfIneligible } from '../hooks/useRedirectIfIneligible';
import { useQueryParamTerm, useQueryParamsEffect } from '../utils/urls/appQueryParamUtilsLegacy';
import { ErrorBoundary } from '../v2/components/error-boundary/ErrorBoundary';
import { ERROR_SEVERITY } from '../v2/components/error-boundary/ErrorSeverity';
import withErrorBoundary from '../v2/components/error-boundary/withErrorBoundary';
import PhoneSupportFooter from '../v2/components/phone-support/PhoneSupportFooter';
import { SCREENS } from '../v2/constants/tracking/Screens';
import { SMART_CRM_CARD, TRACKING_SOURCES } from '../v2/constants/tracking/TrackingSources';
import { IntegrationsSection } from '../v2/containers/IntegrationsSection';
import { trackPageView } from '../v2/utils/tracking/trackingEventUtils';
import MissingInfoFallbackPage from './MissingInfoFallbackPage';
import PhoneSupportMiddle from '../v2/components/phone-support/PhoneSupportMiddle';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PRODUCT_LEVELS_MAP = {
  [Products.CORE_SEATS_PROFESSIONAL]: PROFESSIONAL,
  [Products.CORE_SEATS_ENTERPRISE]: ENTERPRISE
};
const PRODUCTS_TO_SHOW = [Products.CORE_SEATS_PROFESSIONAL, Products.CORE_SEATS_ENTERPRISE];
const SmartCrmPage = () => {
  useAnchorLinkScroll();
  useDocumentTitle(NavItemEnum.SMART_CRM);
  const isAppReady = useGlobalLoading();
  const {
    hasReleaseInsight: isUngatedForSmartCrm,
    loading: isLoadingReleaseInsight,
    error: isErrorLoadingReleaseInsight
  } = useHasReleaseInsight({
    releaseInsight: STANDALONE_CORE_SEATS
  });
  const navItem = NavItemEnum.SMART_CRM;
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  const featureTableProductLevels = PRODUCTS_TO_SHOW.map(apiName => {
    return PRODUCT_LEVELS_MAP[apiName];
  });
  const redirectCondition = useCallback(() => {
    if (isLoadingReleaseInsight) return;
    return !isUngatedForSmartCrm || !!isErrorLoadingReleaseInsight;
  }, [isLoadingReleaseInsight, isUngatedForSmartCrm, isErrorLoadingReleaseInsight]);
  useRedirectIfIneligible({
    condition: redirectCondition
  });
  useEffect(() => {
    if (!isAppReady) return;
    trackPageView({
      screen: navItem
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAppReady]);
  if (!isAppReady) return /*#__PURE__*/_jsx(LoadingScreen, {});
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    fallback: /*#__PURE__*/_jsx(MissingInfoFallbackPage, {
      navItem: navItem
    }),
    errorBoundaryName: SMART_CRM_PAGE_CONTENT,
    severity: ERROR_SEVERITY.HIGH,
    metricsOptions: {
      route: navItem
    },
    children: /*#__PURE__*/_jsxs(View, {
      as: "div",
      "data-selenium-id": "page-success",
      children: [/*#__PURE__*/_jsx(NavMarker, {
        name: 'SMART_CRM_PAGE'
      }), /*#__PURE__*/_jsx(HeaderLayout, {
        selectedNavItem: navItem,
        children: /*#__PURE__*/_jsxs(ProductInfoContainer, {
          children: [/*#__PURE__*/_jsx(BannerSection, {
            screen: SCREENS.SMART_CRM
          }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(PPUIPageHeader, {
            children: [/*#__PURE__*/_jsx(ProductPageTitle, {
              nonProductIdentifier: navItem
            }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
              nonProductIdentifier: navItem
            })]
          }), /*#__PURE__*/_jsx(ProductCardContainer, {
            screen: SCREENS.SMART_CRM,
            productsToShow: PRODUCTS_TO_SHOW,
            trackingSource: SMART_CRM_CARD
          }), /*#__PURE__*/_jsx(PageSection, {
            children: /*#__PURE__*/_jsx(SmartCrmFeatureTable, {
              productLevels: featureTableProductLevels,
              screen: SCREENS.SMART_CRM
            })
          }), /*#__PURE__*/_jsx(PhoneSupportMiddle, {
            phoneSourceId: TRACKING_SOURCES.SMART_CRM_MIDDLE,
            screen: SCREENS.SMART_CRM,
            apiName: Products.CORE_SEATS_PROFESSIONAL
          }), /*#__PURE__*/_jsx(IntegrationsSection, {
            nav: navItem
          }), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
            publicComponentLoader: () => import( /* webpackChunkName: "public-chunk" */`pricing-pages-ui/components/company-proof/CompanyProof`),
            publicComponentProps: {
              tab: ENTERPRISE_TAB,
              screen: SCREENS.SMART_CRM
            }
          }), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
            publicComponentLoader: () => import( /* webpackChunkName: "public-chunk" */`pricing-pages-ui/components/common/ValuePropsSection`)
          }), /*#__PURE__*/_jsx(PageSection, {
            children: /*#__PURE__*/_jsx(SmartCrmFaqs, {})
          }), /*#__PURE__*/_jsx(PhoneSupportFooter, {
            phoneSourceId: TRACKING_SOURCES.SMART_CRM_FOOTER,
            screen: SCREENS.SMART_CRM,
            apiName: Products.CORE_SEATS_PROFESSIONAL
          })]
        })
      })]
    })
  });
};
export default withErrorBoundary(SmartCrmPage, {
  fallback: /*#__PURE__*/_jsx(Redirect, {
    to: createToObject('/suite')
  }),
  errorBoundaryName: SMART_CRM_PAGE,
  severity: ERROR_SEVERITY.HIGH
});